import React, { useState, useEffect } from 'react';
import { X, Sparkles, Upload, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

/**
 * FocusModal - Create or Edit a Focus
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - onSave: function(focusData) - called with { title, description, mission, cover_image_url, status }
 * - focus: object (optional) - if editing, pass existing focus data
 * - mode: 'create' | 'edit'
 */
export default function FocusModal({ isOpen, onClose, onSave, focus = null, mode = 'create' }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    mission: '',
    cover_image_url: '',
    status: 'active' // Always active, no UI for changing
  });
  
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (focus && mode === 'edit') {
      setFormData({
        title: focus.title || '',
        description: focus.description || '',
        mission: focus.mission || '',
        cover_image_url: focus.cover_image_url || '',
        status: 'active' // Always active
      });
      setCoverImagePreview(focus.cover_image_url || null);
    } else {
      // Reset form when creating new
      setFormData({
        title: '',
        description: '',
        mission: '',
        cover_image_url: '',
        status: 'active'
      });
      setCoverImagePreview(null);
    }
    setCoverImageFile(null);
    setErrors({});
  }, [focus, mode, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.title || formData.title.trim().length === 0) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    } else if (formData.title.trim().length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }
    
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }
    
    if (formData.mission && formData.mission.length > 500) {
      newErrors.mission = 'Mission must be less than 500 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors({ image: 'Please select an image file' });
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors({ image: 'Image must be less than 5MB' });
      return;
    }
    
    setCoverImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Clear any image errors
    setErrors(prev => ({ ...prev, image: null }));
  };
  
  const handleRemoveImage = () => {
    setCoverImageFile(null);
    setCoverImagePreview(null);
    setFormData(prev => ({ ...prev, cover_image_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      let imageUrl = formData.cover_image_url;
      
      // Upload image if file was selected
      if (coverImageFile) {
        const fileExt = coverImageFile.name.split('.').pop();
        const fileName = `focus-cover-${Date.now()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('user_uploads')
          .upload(fileName, coverImageFile, {
            cacheControl: '3600',
            upsert: true
          });
        
        if (uploadError) {
          throw new Error(`Failed to upload image: ${uploadError.message}`);
        }
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('user_uploads')
          .getPublicUrl(fileName);
        
        imageUrl = publicUrl;
      }
      
      // Trim all text fields
      const cleanedData = {
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        mission: formData.mission?.trim() || null,
        cover_image_url: imageUrl || null,
        status: 'active' // Always active
      };
      
      await onSave(cleanedData);
      onClose();
    } catch (error) {
      console.error('Error saving focus:', error);
      setErrors({ submit: error.message || 'Failed to save focus' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                {mode === 'create' ? 'Create Focus' : 'Edit Focus'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {mode === 'create' 
                  ? 'A workspace for your ideas, tools, and chats' 
                  : 'Update your Focus details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Japan Trip, Learn Spanish, Fitness Goals"
              className={`w-full px-4 py-3 border rounded-xl text-base focus:outline-none focus:ring-2 transition-all ${
                errors.title
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-200 focus:ring-purple-500'
              }`}
              maxLength={100}
            />
            {errors.title && (
              <p className="mt-2 text-sm text-red-600">{errors.title}</p>
            )}
            <p className="mt-1.5 text-xs text-gray-500">
              {formData.title.length}/100 characters
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="What is this Focus about? (optional)"
              rows={3}
              className={`w-full px-4 py-3 border rounded-xl text-base focus:outline-none focus:ring-2 transition-all resize-none ${
                errors.description
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-200 focus:ring-purple-500'
              }`}
              maxLength={500}
            />
            {errors.description && (
              <p className="mt-2 text-sm text-red-600">{errors.description}</p>
            )}
            <p className="mt-1.5 text-xs text-gray-500">
              {formData.description.length}/500 characters
            </p>
          </div>

          {/* Mission */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Mission
            </label>
            <textarea
              value={formData.mission}
              onChange={(e) => handleChange('mission', e.target.value)}
              placeholder="What do you want to accomplish? (optional)"
              rows={2}
              className={`w-full px-4 py-3 border rounded-xl text-base focus:outline-none focus:ring-2 transition-all resize-none ${
                errors.mission
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-200 focus:ring-purple-500'
              }`}
              maxLength={500}
            />
            {errors.mission && (
              <p className="mt-2 text-sm text-red-600">{errors.mission}</p>
            )}
            <p className="mt-1.5 text-xs text-gray-500">
              {formData.mission.length}/500 characters
            </p>
          </div>

          {/* Cover Image Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Cover Image
            </label>
            
            {!coverImagePreview ? (
              <div>
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload size={32} className="text-gray-400 mb-3" />
                    <p className="mb-2 text-sm text-gray-600">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </label>
                {errors.image && (
                  <p className="mt-2 text-sm text-red-600">{errors.image}</p>
                )}
              </div>
            ) : (
              <div className="relative rounded-xl overflow-hidden border border-gray-200">
                <img 
                  src={coverImagePreview} 
                  alt="Cover preview" 
                  className="w-full h-48 object-cover"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-3 right-3 p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              </div>
            )}
            <p className="mt-1.5 text-xs text-gray-500">
              Optional: Add a cover image to make your Focus more visual
            </p>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg font-medium text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                {mode === 'create' ? 'Create Focus' : 'Save Changes'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

